<template>
  <Breadcrumb></Breadcrumb>
  <div v-if="saleListingAgreement">
    <div class="grid grid-cols-3 items-start gap-4">
      <div class="col-span-2 space-y-4">
        <!-- Title block -->
        <DetailCard>
          <template #default>
            <div class="flex gap-2 items-center">
              <div class="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                <BriefcaseIcon class="h-8 w-8 text-gray-700"></BriefcaseIcon>
              </div>
              <SaleListingAgreementId :saleListingAgreement="saleListingAgreement" />
              <div>
                <RiskScoringLabel :level="riskScoring?.attributes?.scoreLevel ?? undefined" />
              </div>
              <router-link class="text-indigo-600 hover:text-indigo-900 ml-auto transition-transform duration-200 hover:scale-110" :to="{ name: 'edit-sale-listing-agreement', params: { id: saleListingAgreement.id } }">
                {{ $gettext("Editer") }}<span class="sr-only">, {{ saleListingAgreement.id }}</span>
              </router-link>
            </div>
          </template>
        </DetailCard>

        <HighRiskWarningBanner
          v-if="riskScoring?.attributes?.scoreLevel === 'high' && !riskScoring?.attributes?.reviewApproval"
        />

        <!-- Risk scoring factors -->
        <DetailCard>
          <template #default>
            <RiskScoringFactorList
              v-if="riskScoring"
              :riskScoring="riskScoring"
              :riskScoringStore="riskScoringStore"
              :requireHighRiskConfirmation="true"
              :organization="organization"
              @updated="handleRiskScoringUpdate"
            />
          </template>
        </DetailCard>
      </div>

      <div class="col-span-1 flex flex-col gap-4">
        <!-- Chart -->
        <DetailCard :title="$gettext('Aperçu')">
          <template #footer>
            <RiskScoringChart v-if="riskScoring" :riskScoring="riskScoring" />
          </template>
        </DetailCard>

        <!-- Sanctions -->
        <DetailCard v-if="riskScoring">
          <SanctionsBox :riskScoring="riskScoring" />
        </DetailCard>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useSaleListingAgreementStore } from "@/stores/saleListingAgreement.js";
import { useRoute } from "vue-router";
import RiskScoringLabel from "@/components/risk-scorings/RiskScoringLabel.vue";
import RiskScoringFactorList from "@/components/risk-scorings/RiskScoringFactorList.vue";
import { BriefcaseIcon } from "@heroicons/vue/24/outline";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { PieChart } from "echarts/charts";
import { useRiskScoring } from "@/stores/riskScoring";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent
} from "echarts/components";
import Breadcrumb from "@/components/layout/BreadcrumbNavigation.vue";
import RiskScoringChart from "@/components/risk-scorings/RiskScoringChart.vue";
import SanctionsBox from "@/components/risk-scorings/SanctionsBox.vue";
import SaleListingAgreementId from "@/components/sale-listing-agreements/SaleListingAgreementId.vue";
import { DetailCard } from "@/components/ui";
import HighRiskWarningBanner from "@/components/risk-scorings/HighRiskWarningBanner.vue";
import { useOrganizationStore } from "@/stores/organization.js";

use([
  CanvasRenderer,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent
]);

const route = useRoute();
const saleListingAgreementId = route.params.id;
const saleListingAgreementStore = useSaleListingAgreementStore();
const saleListingAgreement = ref(null);

const riskScoringStore = useRiskScoring("sale-listing-agreements", saleListingAgreementId);
const riskScoring = ref(null);
const organizationStore = useOrganizationStore();
const organization = ref(null);

const loadSaleListingsAgreement = async () => {
  saleListingAgreement.value = await saleListingAgreementStore.fetchOne(saleListingAgreementId, null, ["lastRiskScoring"]);
  const orgId = saleListingAgreement.value?.attributes?.organizationId;
  if (orgId && !organization.value) {
    organization.value = await organizationStore.fetchOne(orgId);
  }
};

const handleRiskScoringUpdate = async () => {
  await loadSaleListingsAgreement();
  let riskScoringId = saleListingAgreement.value.relationships.lastRiskScoring.data.id;
  riskScoring.value = await riskScoringStore.fetchOne(riskScoringId);
};

onMounted(async () => {
  await loadSaleListingsAgreement();
  let riskScoringId = saleListingAgreement.value.relationships.lastRiskScoring.data.id;
  riskScoring.value = await riskScoringStore.fetchOne(riskScoringId);
});
</script>
